import { getLinkedPoints } from '@shared/helpers/design/shape.helpers';
import { getMultiPointLineLinkedPointsAboveStorey } from '@shared/helpers/design/mezzanine-helpers';
import {
  findInfiniteLineIntersection,
  shapePointsExceedMaxSeparation,
} from '@shared/helpers/geometry/point-separation.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import type { Shape, Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';

/**
 * A single point relocation produced by a drag, addressed by storey + shape.
 */
export type PointMove = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
  position: Point2D;
};

export type BuildPointMovesParams = {
  /** Storeys snapshot captured at drag start, so repeated updates don't drift. */
  baseStoreys: Storey[];
  /** Storey the user is currently editing. */
  storeyId: Storey['id'];
  /** Shape the dragged point belongs to. */
  shapeId: Shape['id'];
  /** Index of the point under the cursor. */
  pointIndex: number;
  /** Cumulative drag offset in meters. */
  offset: Point2D;
  /**
   * Currently selected point indexes. When two or more are selected (e.g. a
   * line drag selects both endpoints) they all move by `offset`; otherwise only
   * {@link BuildPointMovesParams.pointIndex} moves.
   */
  selectedPointIndexes?: number[];
  /** Segment endpoints moved internally without exposing point selection in UI. */
  lineSegmentPointIndexes?: readonly [number, number];
  /** Exact positions for selected points, used by numeric segment edits. */
  targetPositions?: ReadonlyMap<number, Point2D>;
  /** Preserve the supporting lines of the two neighboring polygon edges. */
  isLineSegmentDrag?: boolean;
  /** Collapse a sub-threshold line segment to one point on mouse release. */
  collapseShortSegmentOnRelease?: boolean;
  /** Offset every supporting line in the polygon by the dragged edge distance. */
  uniformPolygonOffset?: boolean;
  /** Quantize the segment's perpendicular translation to this many meters. */
  segmentTranslationSnapMeters?: number;
  /** Keep upper-storey corners and wall-linked points at their original positions. */
  suppressUpwardPropagation?: boolean;
};

/** A dragged edge shorter than this is collapsed when the pointer is released. */
export const DRAGGED_SEGMENT_COLLAPSE_THRESHOLD_METERS = 0.1;
export const UNIFORM_POLYGON_OFFSET_CLEARANCE_METERS = 0.1;
export const DEFAULT_SEGMENT_TRANSLATION_SNAP_METERS = 0.1;
export const SHIFT_SEGMENT_TRANSLATION_SNAP_METERS = 1;

const snapDistance = (distance: number, incrementMeters?: number): number =>
  incrementMeters && incrementMeters > 0
    ? Math.round(distance / incrementMeters) * incrementMeters
    : distance;

/**
 * Move one polygon edge parallel to itself while retaining the position and
 * direction of its neighboring edge lines. The moved endpoints are the
 * intersections between the translated edge and those two fixed lines.
 */
export const resolveLineSegmentDragPositions = (
  points: readonly Point2D[],
  selectedPointIndexes: readonly number[],
  offset: Point2D,
  options?: {
    collapseShortSegmentOnRelease?: boolean;
    snapIncrementMeters?: number;
  },
): ReadonlyMap<number, Point2D> | undefined => {
  if (points.length < 3 || selectedPointIndexes.length !== 2) return undefined;

  const [firstIndex, secondIndex] = selectedPointIndexes;
  if (firstIndex === undefined || secondIndex === undefined) return undefined;

  let startIndex: number;
  let endIndex: number;
  if ((firstIndex + 1) % points.length === secondIndex) {
    startIndex = firstIndex;
    endIndex = secondIndex;
  } else if ((secondIndex + 1) % points.length === firstIndex) {
    startIndex = secondIndex;
    endIndex = firstIndex;
  } else {
    return undefined;
  }

  const previousIndex = (startIndex - 1 + points.length) % points.length;
  const nextIndex = (endIndex + 1) % points.length;
  const previousPoint = points[previousIndex];
  const startPoint = points[startIndex];
  const endPoint = points[endIndex];
  const nextPoint = points[nextIndex];
  if (!previousPoint || !startPoint || !endPoint || !nextPoint) {
    return undefined;
  }

  const segmentDx = endPoint[0] - startPoint[0];
  const segmentDy = endPoint[1] - startPoint[1];
  const segmentLength = Math.hypot(segmentDx, segmentDy);
  if (!(segmentLength > 0)) return undefined;
  const normalX = -segmentDy / segmentLength;
  const normalY = segmentDx / segmentLength;
  const rawNormalDistance = offset[0] * normalX + offset[1] * normalY;
  const normalDistance = snapDistance(
    rawNormalDistance,
    options?.snapIncrementMeters,
  );
  const normalOffset: Point2D = [
    normalX * normalDistance,
    normalY * normalDistance,
  ];
  const translatedStart: Point2D = [
    startPoint[0] + normalOffset[0],
    startPoint[1] + normalOffset[1],
  ];
  const translatedEnd: Point2D = [
    endPoint[0] + normalOffset[0],
    endPoint[1] + normalOffset[1],
  ];
  const resolvedStart = findInfiniteLineIntersection(
    previousPoint,
    startPoint,
    translatedStart,
    translatedEnd,
  );
  const resolvedEnd = findInfiniteLineIntersection(
    endPoint,
    nextPoint,
    translatedStart,
    translatedEnd,
  );
  if (!resolvedStart || !resolvedEnd) return undefined;

  const originalDx = endPoint[0] - startPoint[0];
  const originalDy = endPoint[1] - startPoint[1];
  const originalLength = Math.hypot(originalDx, originalDy);
  if (!(originalLength > 0)) return undefined;

  const unitX = originalDx / originalLength;
  const unitY = originalDy / originalLength;
  const resolvedSignedLength =
    (resolvedEnd[0] - resolvedStart[0]) * unitX +
    (resolvedEnd[1] - resolvedStart[1]) * unitY;

  const canRemoveSegment = points.length > 3;
  const shouldCollapse =
    options?.collapseShortSegmentOnRelease === true &&
    canRemoveSegment &&
    resolvedSignedLength < DRAGGED_SEGMENT_COLLAPSE_THRESHOLD_METERS;
  const minimumPreviewLength = canRemoveSegment
    ? 0
    : DRAGGED_SEGMENT_COLLAPSE_THRESHOLD_METERS;
  const targetLength = shouldCollapse ? 0 : minimumPreviewLength;

  /** Drop a drag whose resulting shape has a pair farther than 5000 m. */
  const positionsWithinSeparation = (
    positions: ReadonlyMap<number, Point2D>,
  ): ReadonlyMap<number, Point2D> | undefined => {
    const nextPoints = points.map(
      (point, index) => positions.get(index) ?? point,
    );
    return shapePointsExceedMaxSeparation(nextPoints) ? undefined : positions;
  };

  if (shouldCollapse || resolvedSignedLength < targetLength) {
    const lengthDelta = originalLength - resolvedSignedLength;
    const ratio =
      originalLength > targetLength && lengthDelta > 0
        ? (originalLength - targetLength) / lengthDelta
        : 0;
    const clampedRatio = Math.max(
      0,
      shouldCollapse ? ratio : Math.min(1, ratio),
    );

    return positionsWithinSeparation(
      new Map([
        [
          startIndex,
          [
            startPoint[0] + (resolvedStart[0] - startPoint[0]) * clampedRatio,
            startPoint[1] + (resolvedStart[1] - startPoint[1]) * clampedRatio,
          ] satisfies Point2D,
        ],
        [
          endIndex,
          [
            endPoint[0] + (resolvedEnd[0] - endPoint[0]) * clampedRatio,
            endPoint[1] + (resolvedEnd[1] - endPoint[1]) * clampedRatio,
          ] satisfies Point2D,
        ],
      ]),
    );
  }

  return positionsWithinSeparation(
    new Map([
      [startIndex, resolvedStart],
      [endIndex, resolvedEnd],
    ]),
  );
};

const pointToSegmentDistance = (
  point: Point2D,
  start: Point2D,
  end: Point2D,
): number => {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const squaredLength = dx * dx + dy * dy;
  if (!(squaredLength > 0)) {
    return Math.hypot(point[0] - start[0], point[1] - start[1]);
  }
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / squaredLength,
    ),
  );
  return Math.hypot(
    point[0] - (start[0] + dx * ratio),
    point[1] - (start[1] + dy * ratio),
  );
};

const orientation = (first: Point2D, second: Point2D, third: Point2D): number =>
  (second[0] - first[0]) * (third[1] - first[1]) -
  (second[1] - first[1]) * (third[0] - first[0]);

const isPointOnSegment = (
  point: Point2D,
  start: Point2D,
  end: Point2D,
): boolean =>
  Math.abs(orientation(start, end, point)) <= 1e-9 &&
  point[0] >= Math.min(start[0], end[0]) - 1e-9 &&
  point[0] <= Math.max(start[0], end[0]) + 1e-9 &&
  point[1] >= Math.min(start[1], end[1]) - 1e-9 &&
  point[1] <= Math.max(start[1], end[1]) + 1e-9;

const segmentsIntersect = (
  firstStart: Point2D,
  firstEnd: Point2D,
  secondStart: Point2D,
  secondEnd: Point2D,
): boolean => {
  const firstA = orientation(firstStart, firstEnd, secondStart);
  const firstB = orientation(firstStart, firstEnd, secondEnd);
  const secondA = orientation(secondStart, secondEnd, firstStart);
  const secondB = orientation(secondStart, secondEnd, firstEnd);
  const crosses =
    ((firstA > 0 && firstB < 0) || (firstA < 0 && firstB > 0)) &&
    ((secondA > 0 && secondB < 0) || (secondA < 0 && secondB > 0));
  if (crosses) return true;

  return (
    isPointOnSegment(secondStart, firstStart, firstEnd) ||
    isPointOnSegment(secondEnd, firstStart, firstEnd) ||
    isPointOnSegment(firstStart, secondStart, secondEnd) ||
    isPointOnSegment(firstEnd, secondStart, secondEnd)
  );
};

const getMinimumNonAdjacentSegmentDistance = (
  points: readonly Point2D[],
): number => {
  let minimum = Number.POSITIVE_INFINITY;
  for (let firstIndex = 0; firstIndex < points.length; firstIndex += 1) {
    const firstStart = points[firstIndex];
    const firstEnd = points[(firstIndex + 1) % points.length];
    if (!firstStart || !firstEnd) continue;
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < points.length;
      secondIndex += 1
    ) {
      if (
        secondIndex === firstIndex + 1 ||
        (firstIndex === 0 && secondIndex === points.length - 1)
      ) {
        continue;
      }
      const secondStart = points[secondIndex];
      const secondEnd = points[(secondIndex + 1) % points.length];
      if (!secondStart || !secondEnd) continue;
      const distance = segmentsIntersect(
        firstStart,
        firstEnd,
        secondStart,
        secondEnd,
      )
        ? 0
        : Math.min(
            pointToSegmentDistance(firstStart, secondStart, secondEnd),
            pointToSegmentDistance(firstEnd, secondStart, secondEnd),
            pointToSegmentDistance(secondStart, firstStart, firstEnd),
            pointToSegmentDistance(secondEnd, firstStart, firstEnd),
          );
      minimum = Math.min(minimum, distance);
    }
  }
  return minimum;
};

const buildUniformOffsetPoints = (
  points: readonly Point2D[],
  distanceMeters: number,
): Point2D[] | undefined => {
  const signedAreaTwice = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
  }, 0);
  if (Math.abs(signedAreaTwice) < 1e-9) return undefined;
  const windingSign = signedAreaTwice > 0 ? 1 : -1;
  const offsetEdges = points.map((start, index) => {
    const end = points[(index + 1) % points.length];
    if (!end) return undefined;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const length = Math.hypot(dx, dy);
    if (!(length > 1e-9)) return undefined;
    const offset: Point2D = [
      (dy / length) * windingSign * distanceMeters,
      (-dx / length) * windingSign * distanceMeters,
    ];
    return {
      start: [start[0] + offset[0], start[1] + offset[1]] as Point2D,
      end: [end[0] + offset[0], end[1] + offset[1]] as Point2D,
    };
  });
  if (offsetEdges.some((edge) => !edge)) return undefined;

  return points.flatMap((point, index) => {
    const previousEdge =
      offsetEdges[(index - 1 + points.length) % points.length];
    const currentEdge = offsetEdges[index];
    if (!previousEdge || !currentEdge) return [];
    const intersection = findInfiniteLineIntersection(
      previousEdge.start,
      previousEdge.end,
      currentEdge.start,
      currentEdge.end,
    );
    if (intersection) return [intersection];
    const previousOffset: Point2D = [
      previousEdge.end[0] - point[0],
      previousEdge.end[1] - point[1],
    ];
    return [
      [point[0] + previousOffset[0], point[1] + previousOffset[1]] as Point2D,
    ];
  });
};

/** Offset every polygon edge equally, clamped before short or overlapping edges. */
export const resolveUniformPolygonOffsetPositions = (
  points: readonly Point2D[],
  draggedStartIndex: number,
  offset: Point2D,
  options?: { snapIncrementMeters?: number },
): ReadonlyMap<number, Point2D> | undefined => {
  if (points.length < 3) return undefined;
  const start = points[draggedStartIndex];
  const end = points[(draggedStartIndex + 1) % points.length];
  if (!start || !end) return undefined;
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 1e-9)) return undefined;
  const signedAreaTwice = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
  }, 0);
  if (Math.abs(signedAreaTwice) < 1e-9) return undefined;
  const windingSign = signedAreaTwice > 0 ? 1 : -1;
  const outwardNormal: Point2D = [
    (dy / length) * windingSign,
    (-dx / length) * windingSign,
  ];
  const rawDistance =
    offset[0] * outwardNormal[0] + offset[1] * outwardNormal[1];
  const requestedDistance = snapDistance(
    rawDistance,
    options?.snapIncrementMeters,
  );
  const baseClearance = getMinimumNonAdjacentSegmentDistance(points);
  const requiredClearance = Math.min(
    UNIFORM_POLYGON_OFFSET_CLEARANCE_METERS,
    baseClearance,
  );
  const isValid = (candidate: readonly Point2D[]): boolean =>
    !shapePointsExceedMaxSeparation(candidate) &&
    !isPolygonSelfIntersecting(candidate) &&
    candidate.every((point, index) => {
      const next = candidate[(index + 1) % candidate.length];
      const basePoint = points[index];
      const baseNext = points[(index + 1) % points.length];
      return (
        next !== undefined &&
        basePoint !== undefined &&
        baseNext !== undefined &&
        Math.hypot(next[0] - point[0], next[1] - point[1]) >=
          DRAGGED_SEGMENT_COLLAPSE_THRESHOLD_METERS &&
        (next[0] - point[0]) * (baseNext[0] - basePoint[0]) +
          (next[1] - point[1]) * (baseNext[1] - basePoint[1]) >
          0
      );
    }) &&
    getMinimumNonAdjacentSegmentDistance(candidate) >= requiredClearance;

  let resolvedPoints = buildUniformOffsetPoints(points, requestedDistance);
  if (!resolvedPoints) return undefined;
  if (!isValid(resolvedPoints)) {
    const basePoints = buildUniformOffsetPoints(points, 0);
    if (!basePoints || !isValid(basePoints)) return undefined;
    resolvedPoints = basePoints;
    let validDistance = 0;
    let invalidDistance = requestedDistance;
    for (let iteration = 0; iteration < 24; iteration += 1) {
      const candidateDistance = (validDistance + invalidDistance) / 2;
      const candidate = buildUniformOffsetPoints(points, candidateDistance);
      if (candidate && isValid(candidate)) {
        validDistance = candidateDistance;
        resolvedPoints = candidate;
      } else {
        invalidDistance = candidateDistance;
      }
    }
  }
  return new Map(resolvedPoints.map((point, index) => [index, point]));
};

/** True when releasing this drag should replace the short edge with one point. */
export const shouldCollapseLineSegmentDrag = (
  points: readonly Point2D[],
  startIndex: number,
  offset: Point2D,
  snapIncrementMeters?: number,
): boolean => {
  if (points.length <= 3) return false;
  const endIndex = (startIndex + 1) % points.length;
  const positions = resolveLineSegmentDragPositions(
    points,
    [startIndex, endIndex],
    offset,
    { snapIncrementMeters },
  );
  const start = positions?.get(startIndex);
  const end = positions?.get(endIndex);
  if (!start || !end) return false;
  return (
    Math.hypot(end[0] - start[0], end[1] - start[1]) <
    DRAGGED_SEGMENT_COLLAPSE_THRESHOLD_METERS
  );
};

/**
 * Resolve the dragged edge's signed perpendicular displacement. Positive means
 * outward from the polygon and negative means inward. Uses the clamped segment
 * positions so the reported distance matches the visible geometry.
 */
export const getSignedLineSegmentDragDistance = (
  points: readonly Point2D[],
  startIndex: number,
  offset: Point2D,
  options?: {
    uniformPolygonOffset?: boolean;
    snapIncrementMeters?: number;
  },
): number | undefined => {
  if (points.length < 3 || startIndex < 0 || startIndex >= points.length) {
    return undefined;
  }

  const endIndex = (startIndex + 1) % points.length;
  const startPoint = points[startIndex];
  const endPoint = points[endIndex];
  if (!startPoint || !endPoint) return undefined;

  const resolvedPositions = options?.uniformPolygonOffset
    ? resolveUniformPolygonOffsetPositions(points, startIndex, offset, {
        snapIncrementMeters: options.snapIncrementMeters,
      })
    : resolveLineSegmentDragPositions(points, [startIndex, endIndex], offset, {
        snapIncrementMeters: options?.snapIncrementMeters,
      });
  const resolvedStart = resolvedPositions?.get(startIndex);
  if (!resolvedStart) return undefined;

  const dx = endPoint[0] - startPoint[0];
  const dy = endPoint[1] - startPoint[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return undefined;

  const signedAreaTwice = points.reduce((sum, current, index) => {
    const next = points[(index + 1) % points.length];
    if (!next) return sum;
    return sum + current[0] * next[1] - next[0] * current[1];
  }, 0);
  if (Math.abs(signedAreaTwice) < 1e-9) return undefined;

  const windingSign = signedAreaTwice > 0 ? 1 : -1;
  const outwardNormalX = (dy / length) * windingSign;
  const outwardNormalY = (-dx / length) * windingSign;

  return (
    (resolvedStart[0] - startPoint[0]) * outwardNormalX +
    (resolvedStart[1] - startPoint[1]) * outwardNormalY
  );
};

/**
 * Resolve every point that must move for a point or line drag.
 *
 * Beyond the dragged point(s) this includes two kinds of upward propagation, so
 * a building never comes apart vertically while editing a lower storey:
 * - **exact-position links** via {@link getLinkedPoints} — corners on storeys
 *   at or above the edited one sitting on the identical position follow along.
 * - **line links** via {@link getMultiPointLineLinkedPointsAboveStorey} — points
 *   on upper storeys lying *on* a dragged wall segment are re-projected onto
 *   that segment's new position.
 *
 * Mezzanines are excluded from upward propagation by the underlying helpers,
 * since a mezzanine is bounded by its parent storey rather than shared with the
 * storeys above.
 *
 * @returns Deduplicated moves for the selected/dragged indices, then linked points.
 */
export const buildPointMoves = ({
  baseStoreys,
  storeyId,
  shapeId,
  pointIndex,
  offset,
  selectedPointIndexes,
  lineSegmentPointIndexes,
  targetPositions,
  isLineSegmentDrag,
  collapseShortSegmentOnRelease,
  uniformPolygonOffset,
  segmentTranslationSnapMeters,
  suppressUpwardPropagation,
}: BuildPointMovesParams): PointMove[] => {
  const baseStorey = baseStoreys.find((storey) => storey.id === storeyId);
  const baseShape = baseStorey?.shapes?.find((shape) => shape.id === shapeId);
  if (!baseShape?.points) return [];

  // Fall back to the single dragged point when no multi-selection exists.
  const indices = uniformPolygonOffset
    ? baseShape.points.map((_, index) => index)
    : (lineSegmentPointIndexes ??
      (selectedPointIndexes && selectedPointIndexes.length > 1
        ? selectedPointIndexes
        : [pointIndex]));

  const [dx, dy] = offset;
  const segmentPositions =
    targetPositions ??
    (isLineSegmentDrag && uniformPolygonOffset
      ? resolveUniformPolygonOffsetPositions(
          baseShape.points,
          pointIndex,
          offset,
          { snapIncrementMeters: segmentTranslationSnapMeters },
        )
      : isLineSegmentDrag
        ? resolveLineSegmentDragPositions(baseShape.points, indices, offset, {
            collapseShortSegmentOnRelease,
            snapIncrementMeters: segmentTranslationSnapMeters,
          })
        : undefined);
  if (isLineSegmentDrag && !segmentPositions) return [];
  const moves: PointMove[] = [];
  const seen = new Set<string>();

  const addMove = (move: PointMove): void => {
    const key = `${move.storeyId}:${move.shapeId}:${move.pointIndex}`;
    if (seen.has(key)) return;
    seen.add(key);
    moves.push(move);
  };

  for (const index of indices) {
    const basePoint = baseShape.points[index];
    if (!basePoint) continue;
    const position: Point2D = segmentPositions?.get(index) ?? [
      basePoint[0] + dx,
      basePoint[1] + dy,
    ];
    addMove({ storeyId, shapeId, pointIndex: index, position });

    // Corners on upper storeys sharing this exact position move identically.
    const linkedPoints = getLinkedPoints({
      storeys: baseStoreys,
      storeyId,
      shapeId,
      pointIndex: index,
    });
    for (const linked of linkedPoints) {
      if (!suppressUpwardPropagation || linked.storeyId === storeyId) {
        addMove({ ...linked, position });
      }
    }
  }

  // Points resting on a dragged segment slide along its new position.
  const lineLinkedPoints = getMultiPointLineLinkedPointsAboveStorey({
    baseStoreys,
    storeyId,
    shapeId,
    movedIndices: new Set(indices),
    offset: [dx, dy],
    movedPositions: segmentPositions,
  });
  for (const linked of lineLinkedPoints) {
    if (!suppressUpwardPropagation || linked.storeyId === storeyId) {
      addMove(linked);
    }
  }

  return moves;
};
